/**
 * Photo Studio Scene Presets
 *
 * Scene categories and templates for the Product Photo Studio.
 * Each scene has a promptTemplate that provides detailed photography direction
 * to the image generation API.
 */

export const SCENE_CATEGORIES = [
    {
        id: 'ecommerce',
        name: 'E-Commerce',
        scenes: [
            {
                id: 'studio-white',
                name: 'Studio White',
                description: 'Clean white background product shot',
                promptTemplate: 'Professional studio product photography on a pure white seamless background. Clean, well-lit, commercial-quality product shot. Soft shadow underneath. Shot from a slight 3/4 angle to show dimension. High-key studio lighting with soft diffusion.',
            },
            {
                id: 'lifestyle',
                name: 'Lifestyle',
                description: 'Product in a real-world setting',
                promptTemplate: 'Lifestyle product photography showing the product in its natural usage context. Warm, inviting environment. Styled with complementary props and surfaces. Natural or warm studio lighting. Magazine-quality editorial feel.',
            },
            {
                id: 'flat-lay',
                name: 'Flat Lay',
                description: 'Top-down styled arrangement',
                promptTemplate: 'Overhead flat lay product photography. The product is the hero piece arranged on a styled surface with complementary props (plants, textures, stationery). Top-down camera angle. Soft, even lighting. Instagram-worthy composition with balanced negative space.',
            },
            {
                id: 'model-holding',
                name: 'Model Holding',
                description: 'Person interacting with product',
                promptTemplate: 'Product photography showing a person naturally holding or using the product. Focus on the product while the person provides scale and context. Lifestyle setting with soft bokeh background. The product must be the focal point with sharp detail.',
            },
            {
                id: 'unboxing',
                name: 'Unboxing',
                description: 'Product emerging from packaging',
                promptTemplate: 'Unboxing product photography. The product is shown partially emerging from premium packaging. Tissue paper, branded elements, and the excitement of discovery. Elevated angle showing both the product and the packaging experience.',
            },
            {
                id: 'scale-comparison',
                name: 'Scale / Size',
                description: 'Product shown with size reference',
                promptTemplate: 'Product photography showing the product next to common objects for scale reference (hand, pen, coffee cup). Clean background with the product clearly visible. Helps viewers understand the actual size of the product.',
            },
        ]
    },
    {
        id: 'social',
        name: 'Social Media',
        scenes: [
            {
                id: 'instagram-aesthetic',
                name: 'Instagram Aesthetic',
                description: 'Curated, feed-ready shot',
                promptTemplate: 'Instagram-aesthetic product photography. Curated color palette with harmonious tones. Styled with on-trend props and textures. Golden hour or soft diffused lighting. Highly shareable, feed-worthy composition with muted warm tones.',
            },
            {
                id: 'tiktok-trending',
                name: 'TikTok Trending',
                description: 'Bold, attention-grabbing',
                promptTemplate: 'Bold, vibrant product shot optimized for TikTok. High contrast, saturated colors. Dynamic angle or unexpected perspective. Pop culture inspired styling. Eye-catching composition that stops the scroll. Gen-Z aesthetic with bold typography-friendly space.',
            },
            {
                id: 'pinterest-mood',
                name: 'Pinterest Mood',
                description: 'Aspirational board aesthetic',
                promptTemplate: 'Pinterest-worthy product photography with aspirational, editorial quality. Soft muted color palette. Beautifully styled vignette with complementary objects. Dreamy lighting with soft shadows. Save-worthy composition.',
            },
            {
                id: 'story-vertical',
                name: 'Story-Ready',
                description: 'Optimized for Stories/Reels',
                promptTemplate: 'Vertical-format product photography optimized for Instagram Stories and Reels. The product is centered with generous space above and below for text overlays. Bold, clean composition. Vibrant colors that pop on mobile screens.',
            },
        ]
    },
    {
        id: 'seasonal',
        name: 'Seasonal',
        scenes: [
            {
                id: 'holiday-winter',
                name: 'Holiday / Winter',
                description: 'Festive winter setting',
                promptTemplate: 'Holiday-themed product photography. Festive winter setting with pine branches, warm bokeh lights, ribbons, and subtle snow elements. Warm, cozy lighting. Gift-giving context. Premium holiday catalog feel.',
            },
            {
                id: 'spring-fresh',
                name: 'Spring / Fresh',
                description: 'Light, airy atmosphere',
                promptTemplate: 'Spring-themed product photography. Fresh, light atmosphere with floral elements, pastel tones, and natural daylight. Clean and airy feel. Blooming flowers or green leaves as accents. Renewal and freshness theme.',
            },
            {
                id: 'summer-vibrant',
                name: 'Summer / Vibrant',
                description: 'Bright, energetic vibes',
                promptTemplate: 'Summer-themed product photography. Bright sunlight, tropical elements, vibrant colors. Outdoor or beach-inspired setting. High energy, warm tones. Sunlit and joyful atmosphere.',
            },
            {
                id: 'autumn-cozy',
                name: 'Autumn / Cozy',
                description: 'Warm, earthy fall tones',
                promptTemplate: 'Autumn-themed product photography. Warm earthy tones — amber, rust, deep orange, forest green. Cozy setting with natural textures like wood, knitted fabric, dried leaves. Golden hour warm lighting.',
            },
            {
                id: 'valentines',
                name: "Valentine's Day",
                description: 'Romantic, gift-worthy',
                promptTemplate: "Valentine's Day product photography. Romantic setting with soft pinks, reds, and gold accents. Rose petals, heart-shaped elements, premium gift wrapping. Warm, intimate lighting. Gift-giving context.",
            },
            {
                id: 'black-friday',
                name: 'Black Friday / Sale',
                description: 'Bold, promotional feel',
                promptTemplate: 'Black Friday promotional product photography. Bold dark background with dramatic gold or neon accent lighting. Premium product shot with high contrast. Space for "SALE" or percentage overlays. Urgent, exclusive feel.',
            },
        ]
    }
];

export const ASPECT_RATIOS = [
    { value: '1:1', label: '1:1', description: 'Square' },
    { value: '4:5', label: '4:5', description: 'Portrait' },
    { value: '9:16', label: '9:16', description: 'Story / Reel' },
    { value: '16:9', label: '16:9', description: 'Landscape' },
    { value: '1.91:1', label: '1.91:1', description: 'Ad Banner' },
    { value: '2:3', label: '2:3', description: 'Pinterest' },
];
